import { AutomateIcon, PencilIcon } from '@eleveo/icons-react';
import { Table, Tooltip } from '@zoom/components';
import { format as formatDate } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { TFunction } from 'i18next';
import React from 'react';
import { Link } from 'react-router-dom';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { EllipsisContent } from '../../components/EllipsisContent';
import { DeleteIcon } from '../../components/ListIcons/DeleteIcon';
import { ProductionIcon } from '../../components/ListIcons/ProductionIcon';
import { ViewIcon } from '../../components/ListIcons/ViewIcon';
import { SortedBulkLabels } from '../../components/SortedBulkLabels';
import { LockType, StateEnum as ProductionStateEnum, ScheduleInfo } from '../../graphql/components';
import { CreateEditView } from '../../types';
import { dateRangeToFormattedString, handleEndOfDateRange } from '../../utils/dateRange';
import { getSchedulePropTag, getListOfSchedulesHeaderTag, LIST_OF_SCHEDULES_CONTAINER_TAG } from './constants';
import { getPathname } from './utils/pathname';

export interface ScheduleType extends ScheduleInfo {
  queues?: {
    id: string;
    name: string;
  }[];
}

export interface SchedulesListProps {
  schedules: ScheduleType[];
  t: TFunction;
  handleDeleteSchedule: (schedule: ScheduleType) => void;
}

const renderSchedules = (
  schedules: ScheduleType[],
  t: TFunction,
  handleDeleteSchedule: (schedule: ScheduleType) => void,
  hasEditPermission: boolean,
  hasDeletePermission: boolean,
  localeObj?: Locale
) => {
  const dateFormat = t('schedule.dateFormat');

  return schedules.map((schedule: ScheduleType, index) => {
    const isProduction = schedule.state === ProductionStateEnum.Production;
    const isLocked = schedule.lockType === LockType.Write;

    // Show range according to schedule's timezone
    const dateFrom = utcToZonedTime(schedule.scheduleRange.startDateTime, schedule.timezone);
    const dateTo = utcToZonedTime(handleEndOfDateRange(schedule.scheduleRange.endDateTime), schedule.timezone);
    const range = dateRangeToFormattedString(dateFrom, dateTo, dateFormat, localeObj);
    const updated = formatDate(new Date(schedule.modificationDate), dateFormat, { locale: localeObj });

    const handleDelete = () => handleDeleteSchedule(schedule);

    const EditScheduleAction = isLocked ? (
      <Tooltip message={t('schedule.autoSchedule.editDisabled')} placement="bottom">
        <PencilIcon
          className="text-xl list-actions__icon edit-disabled"
          data-test-id={getSchedulePropTag(index, `${CreateEditView.EDIT}-disabled`)}
        />
      </Tooltip>
    ) : (
      <Link
        to={{
          pathname: getPathname(CreateEditView.EDIT, schedule.id),
          state: {
            initialScheduleInfo: schedule,
          },
        }}
        data-test-id={getSchedulePropTag(index, CreateEditView.EDIT)}
      >
        <PencilIcon className="text-chart-300 text-xl" />
      </Link>
    );

    return (
      <tr key={schedule.id}>
        <td className="scheduling-list-first-column list-actions">
          <ProductionIcon
            testId={getSchedulePropTag(index, isProduction ? 'in-production' : 'in-draft')}
            /**
             * isProduction needs to be a negated value as we want to show current status, not to revert it
             * (the naming convention came from old implementation, however it seems to be a kind of misleading)
             */
            isProduction={!isProduction}
            tooltipMessageKey={isProduction ? 'layout.states.inProduction' : 'layout.states.inDraft'}
            color={isProduction ? 'green' : 'orange'}
          />
          {schedule.lockType === LockType.Write && (
            <Tooltip message={t('schedule.autoSchedule.inProgress')} placement="bottom">
              <AutomateIcon
                className="text-chart-300 text-2xl"
                data-test-id={getSchedulePropTag(index, 'auto-scheduling-in-progress')}
              />
            </Tooltip>
          )}
        </td>
        <EllipsisContent as="td" className="col-wrap" data-test-id={getSchedulePropTag(index, 'name')}>
          {schedule.name}
        </EllipsisContent>
        <td className="col-wrap" data-test-id={getSchedulePropTag(index, 'queue')}>
          <SortedBulkLabels items={schedule.queues} label={t('queue.queues')} />
        </td>
        <td className="text-center" data-test-id={getSchedulePropTag(index, 'schedule-range')}>
          {range}
        </td>
        <td data-test-id={getSchedulePropTag(index, 'updated')}>{updated}</td>
        <td className="list-actions">
          {hasEditPermission && EditScheduleAction}
          <Link
            to={{
              pathname: getPathname(CreateEditView.VIEW, schedule.id),
              state: {
                initialScheduleInfo: schedule,
              },
            }}
            data-test-id={getSchedulePropTag(index, CreateEditView.VIEW)}
          >
            <ViewIcon />
          </Link>
          {hasDeletePermission && (
            <DeleteIcon
              enabled={!isProduction && !isLocked}
              testId={getSchedulePropTag(index, isProduction || isLocked ? 'delete-disabled' : 'delete')}
              onClick={handleDelete}
              tooltipMessageKey={
                isLocked
                  ? t('schedule.autoSchedule.deleteDisabled')
                  : isProduction
                  ? t('schedule.productionDelete')
                  : undefined
              }
            />
          )}
        </td>
      </tr>
    );
  });
};

const renderScheduleListHeader = (t: TFunction) => (
  <tr>
    <th className="scheduling-list-first-column" data-test-id={getListOfSchedulesHeaderTag('statuses')}>
      {t('dataImport.status.status')}
    </th>
    <th className="col-wrap" data-test-id={getListOfSchedulesHeaderTag('name')}>
      {t('form.name')}
    </th>
    <th className="col-wrap" data-test-id={getListOfSchedulesHeaderTag('queue')}>
      {t('queue.queue')}
    </th>
    <th className="text-center" data-test-id={getListOfSchedulesHeaderTag('schedule-range')}>
      {t('schedule.range')}
    </th>
    <th data-test-id={getListOfSchedulesHeaderTag('updated')}>{t('form.updated')}</th>
    <th data-test-id={getListOfSchedulesHeaderTag('actions')}>{t('layout.action.actions')}</th>
  </tr>
);

export const ScheduleList: React.FC<SchedulesListProps> = ({ schedules = [], t, handleDeleteSchedule }) => {
  const { userHasPermissions, localeObj } = useAuth();
  const hasEditPermission = userHasPermissions([Permissions.EditSchedule]);
  const hasDeletePermission = userHasPermissions([Permissions.DeleteSchedule]);

  return (
    <Table data-test-id={LIST_OF_SCHEDULES_CONTAINER_TAG} striped withContainerPadding>
      <thead>{renderScheduleListHeader(t)}</thead>
      <tbody>
        {renderSchedules(schedules, t, handleDeleteSchedule, hasEditPermission, hasDeletePermission, localeObj)}
      </tbody>
    </Table>
  );
};
